import {
  ColorPicker,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { HookFormColorPicker, HookFormText } from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { ChangeEvent } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useMount } from 'react-use';

import { krakenApi } from 'apis/kraken';
import { tagsApi } from 'apis/tags';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { stringToHexColor } from 'utils/color/color';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './createTagModal.module.scss';

const formId = 'create-tag-form';
const MAX_LENGTH = 255;

type FieldValues = {
  readonly name: string;
  readonly color: string;
};

/** A modal for creating new tags. */
export const CreateTagModal = createAppModal(
  { type: 'app:createTag' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const location = useLocation();

    const [createTag, createState] = tagsApi.useCreateTagMutation();

    const initialName = modal.arg.name ?? '';

    const form = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        name: initialName,
        color: initialName ? stringToHexColor(initialName) : '',
      },
    });

    useMount(() => {
      ampli.tagsCreateModalOpened({
        path: location.pathname,
        tagName: initialName,
      });
    });

    const doesTagAlreadyExist = async (name: string): Promise<boolean> => {
      const response = await dispatch(
        krakenApi.endpoints.readSearchTags.initiate(
          {
            query: name,
            isStrict: true,
          },
          { forceRefetch: true },
        ),
      );

      if (isResponseWithData(response)) {
        return response.data.data.results.some(
          ({ result }) => result.name.toLowerCase() === name.toLowerCase(),
        );
      }

      return false;
    };

    const handleValidSubmit: SubmitHandler<FieldValues> = async (values) => {
      const name = values.name.trim().toLowerCase();
      const tagExists = await doesTagAlreadyExist(name);

      if (tagExists) {
        notify(
          {
            title: 'Tag already exists',
            text: `The tag "${name}" already exists. Please choose a different name.`,
            icon: 'i-exclamation',
          },
          NotificationType.Error,
        );
        return;
      }

      const payload = {
        name: name,
        color: values.color || stringToHexColor(name),
      };

      try {
        const { data: createdTag } = await createTag(payload).unwrap();

        notify(
          {
            title: 'Tag created',
            text: `"${createdTag.name}" tag has been created`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        ampli.tagsCreateModalFormSubmitted({
          tagName: name,
          path: location.pathname,
        });

        meta?.resolve({ tag: createdTag });
        hide?.();
      } catch {
        notify(
          {
            title: 'An error occurred',
            text: `An error occurred when trying to create the "${name}" tag`,
            icon: 'i-exclamation',
          },
          NotificationType.Error,
        );
      }
    };

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const trimmed = event.target.value.trim();

      if (trimmed) {
        form.setValue('color', stringToHexColor(trimmed));
      }
    };

    const handleColorChange = (color: string): void => {
      ampli.tagsCreateModalColorChanged({
        color,
        path: location.pathname,
      });
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead onClose={hide} title="Add a new tag" />

        <ModalBody>
          <form
            id={formId}
            noValidate
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <FormGrid>
              <FormField>
                <HookFormText
                  name="name"
                  label="Name"
                  control={form.control}
                  rules={{
                    required: 'Name is required',
                    minLength: {
                      value: 2,
                      message: 'Must be 2 characters or more',
                    },
                    maxLength: {
                      value: MAX_LENGTH,
                      message: `Must be less than ${MAX_LENGTH} characters`,
                    },
                  }}
                  componentProps={{
                    autoFocus: true,
                    maxLength: MAX_LENGTH,
                    onChange: handleNameChange,
                    style: { textTransform: 'lowercase' },
                  }}
                />
              </FormField>

              <FormField>
                <HookFormColorPicker
                  name="color"
                  label="Tag color"
                  control={form.control}
                  componentProps={{
                    placeholder: 'Select a color',
                    onChange: handleColorChange,
                    colorPickerProps: { alpha: false },
                    dropdownProps: { fixed: true },
                  }}
                >
                  <ColorPicker.Sliders />
                  <ColorPicker.Input />
                </HookFormColorPicker>
              </FormField>
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName="Save"
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            isLoading: createState.isLoading,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
